"use client";

/**
 * Shared client-side fetcher for `/api/user/navbar-data`, deduped at the
 * module level. Several independent components mount per page (NavbarAuth,
 * AdSlotClient's pro-upsell suppression, useAdGuardrails' Pro-respect check)
 * and each used to fire its own request — 2-3 identical GETs per page view.
 * Every consumer now shares ONE in-flight promise per page load.
 *
 * That sharing is what makes the deadline load-bearing (2026-08-21): one
 * stalled request holds EVERY consumer on the page in its not-ready state for
 * as long as the stall lasts, and a hang is not an error, so the `.catch()`
 * below never fires for it. `fetchJsonOrFallback` bounds the wait and resolves
 * to `null` — the same value a 4xx/5xx already produced, which every consumer
 * here already renders correctly (logged-out shape).
 */

import { fetchJsonOrFallback } from "@/lib/net/fetch-with-timeout";

export type NavbarDataResponse = {
	authenticated: boolean;
	email?: string;
	streak?: number;
	isPro?: boolean;
};

let navbarDataPromise: Promise<NavbarDataResponse | null> | null = null;

export function fetchNavbarData(): Promise<NavbarDataResponse | null> {
	if (!navbarDataPromise) {
		navbarDataPromise = fetchJsonOrFallback<NavbarDataResponse | null>(
			"/api/user/navbar-data",
			null,
		);
	}
	return navbarDataPromise;
}

/** Reset the shared cache — test-only. */
export function __resetNavbarDataCacheForTest(): void {
	navbarDataPromise = null;
}
